import { ProvideAuth } from 'module/auth/context/AuthContext';
import React from 'react';
import { createRoot } from 'react-dom/client'; // ✅ React 18 method
import App from './App';
import reportWebVitals from './reportWebVitals';
import { clearOldCacheEntriesByCount } from 'core/utils/localStorageUtils';

// react query setup
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';

// import styles
import { ToastContextProvider } from 'core/components/Toast/context/ToastContext';
import { WorkplacesContextProvider } from 'module/workplaces/context/WorkplacesContext';
import 'rsuite/dist/rsuite.min.css';
import './index.scss';
import './styles/chat.scss';

import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';

import { ThemeProvider } from '@mui/material';
import { theme } from 'styles/mui/theme';
import { notifyApiError, shouldRetryQuery } from 'core/utils/queryErrorHandling';



// Suppress ResizeObserver loop errors (harmless browser quirk; OPS-003).
// FullCalendar v7 relies more heavily on ResizeObserver, so these fire often in dev.
// SEC-008: strip verbose console methods in production so residual logs cannot leak PII.
// Keep warn/error for operational signals (Sentry filters, OPS-003 ResizeObserver guard).
if (process.env.NODE_ENV === 'production') {
	const noop = () => undefined;
	console.log = noop;
	console.debug = noop;
	console.info = noop;
}


const resizeObserverLoopErrRe = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i;

const isResizeObserverLoopError = (value: unknown): boolean => {
	if (typeof value === 'string') {
		return resizeObserverLoopErrRe.test(value);
	}
	if (value && typeof value === 'object' && 'message' in value) {
		return resizeObserverLoopErrRe.test(String((value as { message?: unknown }).message ?? ''));
	}
	return false;
};

// Suppress console.error for ResizeObserver
const originalConsoleError = window.console.error.bind(window.console);
window.console.error = (...args: any[]) => {
	if (args.some((arg) => isResizeObserverLoopError(arg))) {
		return;
	}
	originalConsoleError(...args);
};

// Suppress window.onerror
const originalErrorHandler = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
	if (isResizeObserverLoopError(message) || isResizeObserverLoopError(error)) {
		return true;
	}
};

// CRA / webpack-dev-server overlay uses addEventListener('error'), not only onerror
window.addEventListener(
	'error',
	(event) => {
		if (isResizeObserverLoopError(event.message) || isResizeObserverLoopError(event.error)) {
			event.stopImmediatePropagation();
			event.preventDefault();
		}
	},
	true
);

const queryClient = new QueryClient({
	queryCache: new QueryCache({
		onError: (error) => {
			// List/detail loads often ignore isError and show empty UI — surface a toast (REL-002)
			notifyApiError(error);
		},
	}),
	// Mutations usually show their own toasts; avoid duplicates
	mutationCache: new MutationCache({}),
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			suspense: false,
			// REL-002 UX: offline/network must fail fast — default 3 retries felt like endless loading
			retry: shouldRetryQuery,
		},
		mutations: {
			retry: false,
		},
	},
});

// OPS-001: initialise Sentry only when a DSN is configured for this build.
const sentryDsn = process.env.REACT_APP_SENTRY_DSN?.trim();
if (sentryDsn) {
	Sentry.init({
		dsn: sentryDsn,
		environment: process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV,
		integrations: [new Integrations.BrowserTracing()],
		tracesSampleRate: 0.2,
	});
}

// ✅ React 18 root rendering
const container = document.getElementById('root');
if (container) {
	// Clear old cache entries on app startup to prevent quota issues
	try {
		const cachePrefixes = [
			'tv_package_channels_cache',
			'preference_channels_cache',
			'gracenote_lineups_cache',
		];
		
		cachePrefixes.forEach(prefix => {
			clearOldCacheEntriesByCount(prefix, 3); // Keep only 3 most recent entries per prefix
		});
	} catch (e) {
		console.warn('Failed to clear old cache on startup:', e);
	}

	const root = createRoot(container);
	
	// Wrapper component to conditionally apply StrictMode only in development
	const AppWrapper = () => (
		<ThemeProvider theme={theme}>
			<ToastContextProvider>
				<ProvideAuth>
					<QueryClientProvider client={queryClient}>
						<WorkplacesContextProvider>
							<App />
							<ReactQueryDevtools initialIsOpen={false} />
						</WorkplacesContextProvider>
					</QueryClientProvider>
				</ProvideAuth>
			</ToastContextProvider>
		</ThemeProvider>
	);
	
	root.render(
		process.env.NODE_ENV === 'development' ? (
			<React.StrictMode>
				<AppWrapper />
			</React.StrictMode>
		) : (
			<AppWrapper />
		)
	);
}

reportWebVitals();